'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';

interface SignupFormData {
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

const passwordRequirements = [
  { test: (p: string) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p: string) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p: string) => /[0-9]/.test(p), label: 'One number' },
];

export default function SignupForm({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>({
    mode: 'onBlur',
  });

  const password = watch('password', '');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    // TODO: POST /api/auth/signup — create Supabase auth user + insert business profile row
    // TODO: POST /api/stripe/create-customer — create Stripe customer for billing
    await new Promise((r) => setTimeout(r, 1500));
    setIsLoading(false);
    toast.success('Account created! Redirecting to your dashboard...');
    setTimeout(() => router.push('/decision-dashboard'), 800);
  };

  return (
    <>
      <Toaster position="bottom-right" theme="dark" />
      <div className="animate-slide-up">
        <h2 className="text-2xl font-800 text-white mb-1">Start your free trial</h2>
        <p className="text-[#666] text-sm mb-6">14 days free. No credit card required.</p>

        {/* Google SSO */}
        <button
          type="button"
          className="w-full flex items-center justify-center gap-3 leverage-btn-secondary py-3 mb-5"
          onClick={() => toast.info('Google OAuth — connect Supabase Auth provider')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          <span className="text-sm font-600">Sign up with Google</span>
        </button>

        <div className="flex items-center gap-3 mb-5">
          <hr className="flex-1 border-[#1F1F1F]" />
          <span className="text-[#444] text-xs font-500">or create with email</span>
          <hr className="flex-1 border-[#1F1F1F]" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="leverage-label">First Name *</label>
              <input {...register('firstName', { required: 'Required' })} placeholder="Jordan" className="leverage-input" autoComplete="given-name" />
              {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="leverage-label">Last Name *</label>
              <input {...register('lastName', { required: 'Required' })} placeholder="Wells" className="leverage-input" autoComplete="family-name" />
              {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName.message}</p>}
            </div>
          </div>

          <div>
            <label className="leverage-label">Business Name *</label>
            <input {...register('businessName', { required: 'Business name is required' })} placeholder="Austin Pro Painters" className="leverage-input" autoComplete="organization" />
            {errors.businessName && <p className="text-red-400 text-xs mt-1">{errors.businessName.message}</p>}
          </div>

          <div>
            <label className="leverage-label">Work Email *</label>
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
              type="email"
              placeholder="jordan@austinpropainters.com"
              className="leverage-input"
              autoComplete="email"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="leverage-label">Phone Number *</label>
            <input
              {...register('phone', {
                required: 'Phone is required',
                pattern: { value: /^[\d\s\-\(\)\+]{10,}$/, message: 'Enter a valid phone' },
              })}
              type="tel"
              placeholder="(512) 555-0198"
              className="leverage-input"
              autoComplete="tel"
            />
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
          </div>

          <div>
            <label className="leverage-label">Password *</label>
            <div className="relative">
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Minimum 8 characters' },
                  pattern: { value: /^(?=.*[A-Z])(?=.*[0-9])/, message: 'Must include uppercase and number' },
                })}
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a strong password"
                className="leverage-input pr-11"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>}
            {password.length > 0 && (
              <div className="flex gap-3 mt-2">
                {passwordRequirements.map(({ test, label }) => (
                  <div key={`req-${label}`} className={`flex items-center gap-1 text-[10px] font-500 ${test(password) ? 'text-emerald-400' : 'text-[#444]'}`}>
                    <Check size={10} className={test(password) ? 'text-emerald-400' : 'text-[#333]'} />
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="leverage-label">Confirm Password *</label>
            <div className="relative">
              <input
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (val) => val === password || 'Passwords do not match',
                })}
                type={showConfirm ? 'text' : 'password'}
                placeholder="Repeat your password"
                className="leverage-input pr-11"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors" aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}>
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword.message}</p>}
          </div>

          <div className="flex items-start gap-2.5">
            <input
              {...register('agreeToTerms', { required: 'You must agree to continue' })}
              type="checkbox"
              id="agree-terms"
              className="w-4 h-4 mt-0.5 rounded border-[#3A3A3A] bg-[#0D0D0D] accent-blue-600 flex-shrink-0"
            />
            <label htmlFor="agree-terms" className="text-[#666] text-xs leading-relaxed cursor-pointer">
              I agree to the{' '}
              <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">Privacy Policy</a>
              {' '}of Serious Leverage Inc.
            </label>
          </div>
          {errors.agreeToTerms && <p className="text-red-400 text-xs -mt-2">{errors.agreeToTerms.message}</p>}

          <button
            type="submit"
            disabled={isLoading}
            className="leverage-btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <><Loader2 size={18} className="animate-spin" /> Creating account...</>
            ) : (
              'Create Free Account →'
            )}
          </button>
        </form>

        <p className="text-center text-sm text-[#555] mt-5">
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className="text-blue-400 hover:text-blue-300 font-600 transition-colors">
            Sign in
          </button>
        </p>
      </div>
    </>
  );
}